import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, useConfirm, EmptyState, RelatedLinks, useFocusParam } from '../components/ui';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { routeById } from '../navigation/routes';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import {
  postIdeaApi,
  PostIdea,
  CreatePostIdeaRequest,
  seriesFormatApi,
  SeriesFormat,
  strategyApi,
  StrategyResult,
  GeneratedPostIdeaSuggestion,
  memeApi,
  MemeTemplate,
  MemeTextResult,
} from '../services/api';
import { IdeaMediaPanel } from '../components/media/IdeaMediaPanel';
import { Vorschaubild } from '../components/media/Vorschaubild';
import { useVorschauJeIdee } from '../components/media/useVorschauJeIdee';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

// ----- Meme Modal Component -----
interface MemeModalProps {
  topic: string;
  targetAudience: string;
  onClose: () => void;
}

const MemeModal: React.FC<MemeModalProps> = ({ topic, targetAudience, onClose }) => {
  const [templates, setTemplates] = useState<MemeTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [tone, setTone] = useState<'professional' | 'casual' | 'humorous' | 'inspirational'>('humorous');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [result, setResult] = useState<MemeTextResult | null>(null);
  const [memeError, setMemeError] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [editedTopText, setEditedTopText] = useState('');
  const [editedBottomText, setEditedBottomText] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await memeApi.getTemplates();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch {
      setMemeError('Fehler beim Laden der Templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleGenerateText = async () => {
    setLoading(true);
    setMemeError('');
    setResult(null);

    try {
      const data = await memeApi.generateText({
        topic,
        targetAudience,
        tone,
        templateId: selectedTemplate || undefined,
      });

      if (data.success) {
        setResult(data.result);
        setEditedTopText(data.result.topText);
        setEditedBottomText(data.result.bottomText);
        setStep(2);
      } else {
        setMemeError(data.error || 'Fehler beim Generieren');
      }
    } catch (err: any) {
      setMemeError(err.response?.data?.error || 'Fehler beim Generieren des Meme-Texts');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!result) return;
    setGeneratingImage(true);
    setMemeError('');

    try {
      const data = await memeApi.generateImage({
        templateId: result.template.id,
        imgflipId: result.template.imgflipId,
        topText: editedTopText,
        bottomText: editedBottomText,
      });

      if (data.success) {
        setResult(data.result);
        setStep(3);
      } else {
        setMemeError(data.error || 'Fehler beim Generieren des Bildes');
      }
    } catch (err: any) {
      setMemeError(err.response?.data?.error || 'Fehler beim Generieren des Meme-Bildes');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setResult(null);
    setEditedTopText('');
    setEditedBottomText('');
    setMemeError('');
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface, #fff)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '24px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0 }}>Meme-Generator</h2>
            <p className="text-sm text-muted" style={{ margin: '4px 0 0 0' }}>
              Thema: <strong>{topic}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-secondary btn-sm"
            style={{ minWidth: 'auto', padding: '4px 12px' }}
          >
            X
          </button>
        </div>

        {memeError && (
          <div className="alert alert-error mb-4">{memeError}</div>
        )}

        {/* Step indicator */}
        <div className="flex gap-4 mb-4" style={{ alignItems: 'center' }}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className="flex gap-2"
              style={{
                alignItems: 'center',
                opacity: step >= s ? 1 : 0.4,
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: step >= s ? 'var(--color-primary, #3b82f6)' : 'var(--color-border, #e5e7eb)',
                  color: step >= s ? '#fff' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
              >
                {step > s ? '\u2713' : s}
              </div>
              <span className="text-sm">
                {s === 1 && 'Text generieren'}
                {s === 2 && 'Bearbeiten'}
                {s === 3 && 'Fertig'}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Configuration */}
        {step === 1 && (
          <div>
            <div className="flex gap-4 mb-4 flex-wrap">
              <div className="form-group" style={{ flex: '1 1 200px' }}>
                <label className="form-label">Tonalität</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value as any)}
                  className="form-select"
                  disabled={loading}
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="humorous">Humorous</option>
                  <option value="inspirational">Inspirational</option>
                </select>
              </div>

              <div className="form-group" style={{ flex: '2 1 300px' }}>
                <label className="form-label">Template (optional)</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="form-select"
                  disabled={loading || loadingTemplates}
                >
                  <option value="">Automatisch (KI entscheidet)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} - {t.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template preview thumbnails */}
            {templates.length > 0 && (
              <div className="mb-4">
                <label className="form-label text-sm">Beliebte Templates:</label>
                <div className="flex gap-2 flex-wrap">
                  {templates.slice(0, 6).map((t) => (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTemplate(t.id)}
                      style={{
                        cursor: 'pointer',
                        border: selectedTemplate === t.id
                          ? '2px solid var(--color-primary, #3b82f6)'
                          : '2px solid transparent',
                        borderRadius: '8px',
                        padding: '4px',
                        textAlign: 'center',
                        width: '80px',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      {t.imageUrl ? (
                        <img
                          src={t.imageUrl}
                          alt={t.name}
                          style={{
                            width: '72px',
                            height: '72px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '72px',
                            height: '72px',
                            background: 'var(--color-bg-secondary, #f3f4f6)',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                          }}
                        >
                          {t.name.substring(0, 8)}
                        </div>
                      )}
                      <div className="text-xs mt-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleGenerateText}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Generiere Meme-Text...' : 'Meme-Text generieren'}
            </button>

            {loading && (
              <div className="mt-3 flex gap-2" style={{ alignItems: 'center' }}>
                <div className="loading-spinner" style={{ width: '20px', height: '20px' }}></div>
                <span className="text-sm text-muted">KI erstellt Meme-Text...</span>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Edit text */}
        {step === 2 && result && (
          <div>
            <div className="flex gap-4 flex-wrap">
              {/* Preview */}
              <div style={{ flex: '1 1 300px' }}>
                <div
                  style={{
                    border: '2px solid var(--color-border, #e5e7eb)',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    background: 'var(--color-bg-secondary, #f8f9fa)',
                  }}
                >
                  {result.template.imageUrl ? (
                    <img
                      src={result.template.imageUrl}
                      alt={result.template.name}
                      style={{ width: '100%', maxWidth: '300px', borderRadius: '4px' }}
                    />
                  ) : (
                    <div style={{ padding: '40px 0' }}>
                      <div className="text-sm font-bold">{result.template.name}</div>
                      <div className="text-xs text-muted">{result.template.category}</div>
                    </div>
                  )}
                  <div
                    className="font-bold mt-2"
                    style={{
                      padding: '8px',
                      backgroundColor: 'var(--color-surface, #fff)',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {editedTopText}
                  </div>
                  <div
                    className="font-bold mt-2"
                    style={{
                      padding: '8px',
                      backgroundColor: 'var(--color-surface, #fff)',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {editedBottomText}
                  </div>
                </div>
              </div>

              {/* Edit form */}
              <div style={{ flex: '1 1 300px' }}>
                <div className="form-group">
                  <label className="form-label">Top Text</label>
                  <textarea
                    value={editedTopText}
                    onChange={(e) => setEditedTopText(e.target.value)}
                    className="form-textarea"
                    rows={2}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Bottom Text</label>
                  <textarea
                    value={editedBottomText}
                    onChange={(e) => setEditedBottomText(e.target.value)}
                    className="form-textarea"
                    rows={2}
                  />
                </div>

                <div className="card mb-3" style={{ padding: '10px 14px', background: 'var(--color-bg-secondary, #f8f9fa)' }}>
                  <div className="text-xs text-muted">
                    <strong>Template:</strong> {result.template.name} ({result.template.category})
                  </div>
                  <div className="text-xs text-muted mt-1">{result.explanation}</div>
                </div>

                <div className="btn-group">
                  <button
                    onClick={handleGenerateImage}
                    disabled={generatingImage || !editedTopText.trim() || !editedBottomText.trim()}
                    className="btn btn-success"
                  >
                    {generatingImage ? 'Generiere Bild...' : 'Bild generieren'}
                  </button>
                  <button onClick={handleReset} className="btn btn-secondary">
                    Neu starten
                  </button>
                </div>

                {generatingImage && (
                  <div className="mt-3 flex gap-2" style={{ alignItems: 'center' }}>
                    <div className="loading-spinner" style={{ width: '20px', height: '20px' }}></div>
                    <span className="text-sm text-muted">Erstelle Meme-Bild...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div style={{ textAlign: 'center' }}>
            {result.generatedImageUrl ? (
              <div>
                <img
                  src={result.generatedImageUrl}
                  alt="Generated Meme"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                />
                <div className="mt-4 btn-group" style={{ justifyContent: 'center' }}>
                  <a
                    href={result.generatedImageUrl}
                    download={`meme-${result.template.name.replace(/\s+/g, '-')}.jpg`}
                    className="btn btn-success"
                  >
                    Meme herunterladen
                  </a>
                  <button onClick={handleReset} className="btn btn-primary">
                    Neues Meme erstellen
                  </button>
                  <button onClick={onClose} className="btn btn-secondary">
                    Schliessen
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    border: '2px solid var(--color-success, #10b981)',
                    borderRadius: '8px',
                    padding: '20px',
                    background: 'var(--color-bg-secondary, #f8f9fa)',
                    maxWidth: '400px',
                    margin: '0 auto',
                  }}
                >
                  <div className="font-bold" style={{ fontSize: '1.1em', textTransform: 'uppercase', padding: '8px' }}>
                    {editedTopText}
                  </div>
                  <div className="text-sm text-muted my-2">{result.template.name}</div>
                  <div className="font-bold" style={{ fontSize: '1.1em', textTransform: 'uppercase', padding: '8px' }}>
                    {editedBottomText}
                  </div>
                </div>
                <p className="text-sm text-muted mt-3">
                  Meme-Text generiert. Das Bild konnte nicht erstellt werden (Imgflip-Zugangsdaten fehlen ggf.).
                </p>
                <div className="mt-3 btn-group" style={{ justifyContent: 'center' }}>
                  <button onClick={handleReset} className="btn btn-primary">
                    Neues Meme erstellen
                  </button>
                  <button onClick={onClose} className="btn btn-secondary">
                    Schliessen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ----- Main Page Component -----
const PostIdeasPage: React.FC = () => {
  const darfBearbeiten = useBerechtigung().darf('linkedin.bearbeiten');
  // Beitragsidee, deren Medien gerade aufgeklappt sind.
  const [medienFuer, setMedienFuer] = useState<string | null>(null);
  const [postIdeas, setPostIdeas] = useState<PostIdea[]>([]);
  // Hauptmedium je Idee (Video vor PDF vor Bild) fuer das Vorschaubild.
  const vorschauJeIdee = useVorschauJeIdee(postIdeas.map((i) => i.id));
  const [seriesFormats, setSeriesFormats] = useState<SeriesFormat[]>([]);
  const [strategies, setStrategies] = useState<StrategyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Sprung von Serienformat & Co.: Filter kommen per Query mit.
  const [searchParams] = useSearchParams();
  const [filterSeriesFormat, setFilterSeriesFormat] = useState<string>(() => searchParams.get('seriesFormatId') || '');
  const [filterStatus, setFilterStatus] = useState<string>(() => searchParams.get('status') || '');
  const [formData, setFormData] = useState<CreatePostIdeaRequest>({
    seriesFormatId: '',
    strategyId: '',
    title: '',
    topic: '',
    description: '',
    painPoint: '',
    order: undefined,
  });

  // AI Generation state
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [aiStrategyId, setAiStrategyId] = useState('');
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [aiSeriesFormatId, setAiSeriesFormatId] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [aiProvider, setAiProvider] = useState<'claude' | 'openai' | 'perplexity'>('claude');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<GeneratedPostIdeaSuggestion[]>([]);
  const [aiSelectedIndices, setAiSelectedIndices] = useState<Set<number>>(new Set());
  const [aiSaving, setAiSaving] = useState(false);

  // Meme modal state
  const [memeModalTopic, setMemeModalTopic] = useState<string | null>(null);
  const [memeModalAudience, setMemeModalAudience] = useState('professionals and business leaders');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeriesFormat, filterStatus]);

  // Die aktive Strategie ist die Voreinstellung -- niemand soll sie in einem
  // leeren Dropdown suchen muessen.
  useEffect(() => {
    if (strategies.length === 0) return;
    const preferred = strategies.find((st) => st.isActive) ?? strategies[0];
    setAiStrategyId((cur) => cur || preferred.id);
    setFormData((f) => (f.strategyId ? f : { ...f, strategyId: preferred.id }));
  }, [strategies]);

  // Sprung-Chips von anderen Seiten: ?id=<Idee> scrollt zur Zeile.
  const focusIdeaId = useFocusParam('id', !loading && postIdeas.length > 0);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ideas, formats, strats] = await Promise.all([
        postIdeaApi.getAll({
          seriesFormatId: filterSeriesFormat || undefined,
          status: filterStatus || undefined,
        }),
        seriesFormatApi.getAll(),
        strategyApi.getAll(),
      ]);
      setPostIdeas(ideas);
      setSeriesFormats(formats);
      setStrategies(strats);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Beitragsideen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await postIdeaApi.update(editingId, formData);
      } else {
        await postIdeaApi.create(formData);
      }
      await loadData();
      refresh();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (idea: PostIdea) => {
    setEditingId(idea.id);
    setFormData({
      seriesFormatId: idea.seriesFormatId || '',
      strategyId: idea.strategyId || '',
      title: idea.title,
      topic: idea.topic,
      description: idea.description || '',
      painPoint: idea.painPoint || '',
      order: idea.order,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Beitragsidee löschen?', confirmLabel: 'Löschen', tone: 'danger' });
    if (!ok) return;

    try {
      await postIdeaApi.delete(id);
      await loadData();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await postIdeaApi.update(id, { status: newStatus });
      await loadData();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Status');
    }
  };

  const resetForm = () => {
    setFormData({
      seriesFormatId: '',
      strategyId: '',
      title: '',
      topic: '',
      description: '',
      painPoint: '',
      order: undefined,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getStatusBadgeClass = (status: string) => {
    const statusClasses: Record<string, string> = {
      idea: 'badge badge-neutral',
      planned: 'badge badge-info',
      created: 'badge badge-success',
      scheduled: 'badge badge-warning',
      posted: 'badge badge-primary',
    };
    return statusClasses[status] || 'badge badge-neutral';
  };

  // Meme handlers
  const openMemeModal = (topicText: string, audience?: string) => {
    setMemeModalTopic(topicText);
    if (audience) {
      setMemeModalAudience(audience);
    } else {
      // Try to get audience from selected strategy
      const strategy = getSelectedStrategy();
      setMemeModalAudience(strategy?.targetAudience || 'professionals and business leaders');
    }
  };

  const closeMemeModal = useCallback(() => {
    setMemeModalTopic(null);
  }, []);

  // AI Generation handlers
  const handleAiGenerate = async () => {
    if (!aiStrategyId) {
      setError('Bitte wählen Sie eine Strategie aus');
      return;
    }

    setError('');
    setAiGenerating(true);
    setAiSuggestions([]);
    setAiSelectedIndices(new Set());

    try {
      const suggestions = await postIdeaApi.generate({
        strategyId: aiStrategyId,
        seriesFormatId: aiSeriesFormatId || undefined,
        count: aiCount,
        provider: aiProvider,
      });
      setAiSuggestions(suggestions);
      // Select all by default
      setAiSelectedIndices(new Set(suggestions.map((_, i) => i)));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Generieren der KI-Vorschläge');
    } finally {
      setAiGenerating(false);
    }
  };

  const toggleAiSelection = (index: number) => {
    setAiSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (aiSelectedIndices.size === aiSuggestions.length) {
      setAiSelectedIndices(new Set());
    } else {
      setAiSelectedIndices(new Set(aiSuggestions.map((_, i) => i)));
    }
  };

  const handleSaveSelected = async () => {
    if (aiSelectedIndices.size === 0) {
      setError('Bitte wählen Sie mindestens eine Idee aus');
      return;
    }

    setAiSaving(true);
    setError('');

    try {
      const selectedIdeas: CreatePostIdeaRequest[] = Array.from(aiSelectedIndices).map((i) => ({
        title: aiSuggestions[i].title,
        topic: aiSuggestions[i].topic,
        description: aiSuggestions[i].description,
        painPoint: aiSuggestions[i].painPoint,
        seriesFormatId: aiSeriesFormatId || undefined,
        strategyId: aiStrategyId || undefined,
      }));

      await postIdeaApi.bulkCreate(selectedIdeas);
      await loadData();
      refresh();
      setAiSuggestions([]);
      setAiSelectedIndices(new Set());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Vorschläge');
    } finally {
      setAiSaving(false);
    }
  };

  const getSelectedStrategy = () => {
    return strategies.find((s) => s.id === aiStrategyId);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <FlowStepBar stepId="ideas" />
      {/* Meme Modal */}
      {memeModalTopic && (
        <MemeModal
          topic={memeModalTopic}
          targetAudience={memeModalAudience}
          onClose={closeMemeModal}
        />
      )}

      <PageHeader
        title="Beitragsideen"
        subtitle={routeById('li-ideas')?.description}
        actions={(
          <>
            <button
              onClick={() => {
                setShowAiGenerator(!showAiGenerator);
                if (showForm) setShowForm(false);
              }}
              className={`btn ${showAiGenerator ? 'btn-secondary' : 'btn-warning'}`}
            >
              {showAiGenerator ? 'KI-Generator schliessen' : 'KI-Vorschläge generieren'}
            </button>
            <button
              onClick={() => {
                setShowForm(!showForm);
                if (showAiGenerator) setShowAiGenerator(false);
              }}
              className="btn btn-primary"
            >
              {showForm ? 'Abbrechen' : '+ Neue Beitragsidee'}
            </button>
          </>
        )}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* AI Generator Section */}
      {showAiGenerator && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--color-warning, #f59e0b)' }}>
          <h2 className="form-section-title">KI-Beitragsideen generieren</h2>
          <p className="text-sm text-muted mb-4">
            Generieren Sie automatisch Beitragsideen basierend auf Ihrer Strategie und Ihrem ICP (Ideal Customer Profile).
          </p>

          <div className="flex gap-4 mb-4 flex-wrap">
            <div className="form-group" style={{ flex: '1 1 300px' }}>
              <label className="form-label">
                Strategie & Zielkundenprofil (ICP) *
              </label>
              <select
                value={aiStrategyId}
                onChange={(e) => setAiStrategyId(e.target.value)}
                className="form-select"
                disabled={aiGenerating}
              >
                <option value="">-- Strategie auswählen --</option>
                {strategies.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name} ({strategy.industry} / {strategy.targetAudience})
                  </option>
                ))}
              </select>
              {strategies.length === 0 && (
                <EmptyState
                  compact
                  icon="compass"
                  title="Noch keine Strategie"
                  description="Die KI braucht Zielgruppe und Pain Points als Kontext für gute Ideen."
                  action={{ label: 'Strategie anlegen', to: '/strategy' }}
                />
              )}
            </div>

            <div className="form-group" style={{ flex: '1 1 200px' }}>
              <label className="form-label">
                Serienformat (optional)
              </label>
              <select
                value={aiSeriesFormatId}
                onChange={(e) => setAiSeriesFormatId(e.target.value)}
                className="form-select"
                disabled={aiGenerating}
              >
                <option value="">Kein Serienformat</option>
                {seriesFormats.map((format) => (
                  <option key={format.id} value={format.id}>
                    {format.name} - {format.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ flex: '0 0 120px' }}>
              <label className="form-label">
                Anzahl
              </label>
              <select
                value={aiCount}
                onChange={(e) => setAiCount(parseInt(e.target.value))}
                className="form-select"
                disabled={aiGenerating}
              >
                <option value={3}>3 Ideen</option>
                <option value={5}>5 Ideen</option>
                <option value={8}>8 Ideen</option>
                <option value={10}>10 Ideen</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: '0 0 150px' }}>
              <label className="form-label">
                KI-Provider
              </label>
              <select
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value as 'claude' | 'openai' | 'perplexity')}
                className="form-select"
                disabled={aiGenerating}
              >
                <option value="claude">Claude</option>
                <option value="openai">OpenAI</option>
                <option value="perplexity">Perplexity</option>
              </select>
            </div>
          </div>

          {/* Strategy preview */}
          {aiStrategyId && getSelectedStrategy() && (
            <div className="card mb-4" style={{ padding: '12px 16px', background: 'var(--color-bg-secondary, #f8f9fa)' }}>
              <div className="text-sm">
                <strong>Ausgewählte Strategie:</strong> {getSelectedStrategy()!.name}
                <span className="text-muted"> | Branche: {getSelectedStrategy()!.industry} | Zielgruppe: {getSelectedStrategy()!.targetAudience}</span>
                {getSelectedStrategy()!.icp && (
                  <div className="mt-1 text-muted" style={{ maxHeight: '60px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ICP: {getSelectedStrategy()!.icp!.substring(0, 200)}{getSelectedStrategy()!.icp!.length > 200 ? '...' : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleAiGenerate}
            disabled={!aiStrategyId || aiGenerating}
            className="btn btn-warning"
          >
            {aiGenerating ? 'Generiere Vorschläge...' : 'Vorschläge generieren'}
          </button>

          {aiGenerating && (
            <div className="mt-4" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="loading-spinner" style={{ width: '24px', height: '24px' }}></div>
              <span className="text-muted">KI generiert Beitragsideen basierend auf Ihrer Strategie...</span>
            </div>
          )}

          {/* AI Suggestions */}
          {aiSuggestions.length > 0 && (
            <div className="mt-4">
              <div className="flex gap-4 mb-3" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>
                  {aiSuggestions.length} Vorschläge generiert
                </h3>
                <div className="btn-group">
                  <button
                    onClick={toggleSelectAll}
                    className="btn btn-secondary btn-sm"
                  >
                    {aiSelectedIndices.size === aiSuggestions.length ? 'Keine auswählen' : 'Alle auswählen'}
                  </button>
                  <button
                    onClick={handleSaveSelected}
                    disabled={aiSelectedIndices.size === 0 || aiSaving}
                    className="btn btn-success btn-sm"
                  >
                    {aiSaving
                      ? 'Speichere...'
                      : `${aiSelectedIndices.size} ausgewählte speichern`}
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                {aiSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="card"
                    onClick={() => toggleAiSelection(index)}
                    style={{
                      padding: '16px',
                      cursor: 'pointer',
                      border: aiSelectedIndices.has(index)
                        ? '2px solid var(--color-success, #10b981)'
                        : '2px solid transparent',
                      background: aiSelectedIndices.has(index)
                        ? 'var(--color-bg-success-subtle, #f0fdf4)'
                        : undefined,
                      transition: 'border-color 0.2s, background 0.2s',
                    }}
                  >
                    <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={aiSelectedIndices.has(index)}
                        onChange={() => toggleAiSelection(index)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginTop: '4px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="flex gap-3 mb-1" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '1.05em' }}>{suggestion.title}</strong>
                          <span className="badge badge-neutral">{suggestion.topic}</span>
                        </div>
                        <p className="text-sm mb-1" style={{ margin: '4px 0' }}>
                          {suggestion.description}
                        </p>
                        <div className="flex" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                          <p className="text-sm text-muted" style={{ margin: '4px 0' }}>
                            Pain Point: {suggestion.painPoint}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMemeModal(suggestion.topic);
                            }}
                            className="btn btn-secondary btn-sm"
                            style={{ flexShrink: 0 }}
                            title="Meme fuer diese Idee generieren"
                          >
                            Meme
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 btn-group">
                <button
                  onClick={handleSaveSelected}
                  disabled={aiSelectedIndices.size === 0 || aiSaving}
                  className="btn btn-success"
                >
                  {aiSaving
                    ? 'Speichere...'
                    : `${aiSelectedIndices.size} ausgewählte Ideen speichern`}
                </button>
                <button
                  onClick={handleAiGenerate}
                  disabled={aiGenerating}
                  className="btn btn-warning"
                >
                  Neue Vorschläge generieren
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4 flex-wrap">
        <div className="form-group">
          <label className="form-label">
            Serienformat filtern:
          </label>
          <select
            value={filterSeriesFormat}
            onChange={(e) => setFilterSeriesFormat(e.target.value)}
            className="form-select"
          >
            <option value="">Alle Formate</option>
            {seriesFormats.map((format) => (
              <option key={format.id} value={format.id}>
                {format.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            Status filtern:
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="form-select"
          >
            <option value="">Alle Status</option>
            <option value="idea">Idee</option>
            <option value="planned">Geplant</option>
            <option value="created">Erstellt</option>
            <option value="scheduled">Geplant</option>
            <option value="posted">Gepostet</option>
          </select>
        </div>
      </div>

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">{editingId ? 'Beitragsidee bearbeiten' : 'Neue Beitragsidee erstellen'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="flex gap-4 flex-wrap">
              <div className="form-group" style={{ flex: '1 1 300px' }}>
                <label className="form-label">
                  Strategie & Zielgruppe
                </label>
                <select
                  value={formData.strategyId}
                  onChange={(e) => setFormData({ ...formData, strategyId: e.target.value })}
                  className="form-select"
                >
                  <option value="">Keine Strategie</option>
                  {strategies.map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name} ({strategy.industry} / {strategy.targetAudience})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ flex: '1 1 300px' }}>
                <label className="form-label">
                  Serienformat
                </label>
                <select
                  value={formData.seriesFormatId}
                  onChange={(e) => setFormData({ ...formData, seriesFormatId: e.target.value })}
                  className="form-select"
                >
                  <option value="">Kein Serienformat</option>
                  {seriesFormats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.name} - {format.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Titel / Hook *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="z.B. 5 Fehler bei der Team-Kommunikation"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Thema *
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                required
                placeholder="z.B. Team-Kommunikation"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Kurze Beschreibung der Beitragsidee"
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Pain Point
              </label>
              <textarea
                value={formData.painPoint}
                onChange={(e) => setFormData({ ...formData, painPoint: e.target.value })}
                placeholder="Welcher Schmerzpunkt der Zielgruppe wird adressiert?"
                rows={2}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Reihenfolge (Optional)
              </label>
              <input
                type="number"
                value={formData.order || ''}
                onChange={(e) => setFormData({ ...formData, order: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="z.B. 1, 2, 3..."
                className="form-input"
              />
            </div>

            <div className="btn-group">
              <button
                type="submit"
                className="btn btn-success"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        <h2 className="form-section-title">Alle Beitragsideen ({postIdeas.length})</h2>
        {postIdeas.length === 0 ? (
          <EmptyState
            icon="lightbulb"
            title="Noch keine Beitragsideen"
            description="Lass dir Ideen zu deiner Strategie generieren oder trage eigene ein. Ab etwa 20 Ideen je Serie lohnt sich der Wochenplan."
            action={{ label: 'KI-Generator öffnen', onClick: () => setShowAiGenerator(true) }}
            secondaryAction={{ label: 'Idee manuell anlegen', onClick: () => setShowForm(true) }}
          />
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Thema</th>
                  <th>Strategie</th>
                  <th>Serienformat</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {postIdeas.map((idea) => (
                  <React.Fragment key={idea.id}>
                  <tr data-focus-id={idea.id} aria-current={focusIdeaId === idea.id ? 'true' : undefined}>
                    <td>
                      <div className="mit-vorschau">
                        {vorschauJeIdee[idea.id] && (
                          <Vorschaubild
                            assetId={vorschauJeIdee[idea.id]?.assetId}
                            kind={vorschauJeIdee[idea.id]?.kind}
                            vorschau={vorschauJeIdee[idea.id]?.vorschau}
                            groesse="m"
                            alt={`Vorschau zu „${idea.title}“`}
                          />
                        )}
                        <div className="mit-vorschau__text">
                          <strong>{idea.title}</strong>
                          {idea.description && (
                            <div className="text-sm text-muted mt-1">
                              {idea.description.substring(0, 100)}{idea.description.length > 100 ? '...' : ''}
                            </div>
                          )}
                          <RelatedLinks
                            className="mt-2"
                            items={ideaBacklinks(idea.id, {
                              hasBrief: Boolean(idea.contentBrief),
                              postCount: idea.linkedInPostCount,
                              mediaCount: idea._count?.mediaAssets,
                              flyerCount: idea._count?.flyers,
                              omit: ['idea'],
                            })}
                            label={`Verknüpft mit „${idea.title}“`}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{idea.topic}</td>
                    <td>
                      {idea.strategy ? (
                        <span className="text-sm">
                          {idea.strategy.name}
                        </span>
                      ) : (
                        <span className="text-sm text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {idea.seriesFormat ? (
                        <span className="text-sm">
                          {idea.seriesFormat.name}
                        </span>
                      ) : (
                        <span className="text-sm text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      <select
                        value={idea.status}
                        onChange={(e) => handleStatusChange(idea.id, e.target.value)}
                        className={getStatusBadgeClass(idea.status)}
                      >
                        <option value="idea">Idee</option>
                        <option value="planned">Geplant</option>
                        <option value="created">Erstellt</option>
                        <option value="scheduled">Geplant</option>
                        <option value="posted">Gepostet</option>
                      </select>
                    </td>
                    <td className="text-center">
                      <div className="btn-group justify-center">
                        <button
                          onClick={() => setMedienFuer(medienFuer === idea.id ? null : idea.id)}
                          className="btn btn-secondary btn-sm"
                          title="Videos, PDFs und Bilder dieser Idee"
                          aria-expanded={medienFuer === idea.id}
                        >
                          Medien
                        </button>
                        <button
                          onClick={() => openMemeModal(idea.topic)}
                          className="btn btn-warning btn-sm"
                          title="Meme fuer diese Idee generieren"
                        >
                          Meme
                        </button>
                        <button
                          onClick={() => handleEdit(idea)}
                          className="btn btn-primary btn-sm"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(idea.id)}
                          className="btn btn-danger btn-sm"
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                  {medienFuer === idea.id && (
                    <tr>
                      <td colSpan={6}>
                        <IdeaMediaPanel postIdeaId={idea.id} topic={idea.topic} darfBearbeiten={darfBearbeiten} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostIdeasPage;
